'use client';

/**
 * PaymentMethodsListScreen Component
 *
 * Screen showing list of saved payment methods for selection.
 *
 * Layout:
 * - List of saved payment methods (default gets green border, no arrow)
 * - Separator line
 * - Add payment method buttons (Credit Card, PayPal, etc.)
 *
 * Note: Header with back/close buttons is rendered by parent PurchaseFlow component.
 *
 * PAYG Mode Behavior:
 * - Filters "add" options to hide incompatible kinds (PayPal, Apple Pay, Google Pay)
 * - Shows "Set up Apple Pay" instead of "Continue with Apple Pay"
 *
 * Device Support:
 * - Hides "Continue with Apple Pay/Google Pay" if not supported on device
 * - Shows unsupported saved methods with indicator
 *
 * IAP: Synthetic IAP (default when no saved methods) is not shown here — only on PurchaseDetailsScreen.
 * On this screen we show only real saved methods; user can add IAP via the Add button.
 */

import React, { useState, useEffect, useCallback } from 'react';
import cn from 'classnames';
import type { PaymentMethod, PaymentOption } from '../types';
import { PaymentMethodCard } from '../components/PaymentMethodCard';
import { getKindConfig } from '../config/gatewayRegistry';
import { getPaygIneligibilityTextKey } from '../utils/paygUtils';
import { PURCHASE_FLOW_TEXT_KEYS, PAYMENT_SOURCE_ERRORS } from '../utils/constants';
import { usePurchaseRender } from '../config/PurchaseFlowConfigProvider';
import { usePaymentSupport, useVisibleKinds } from '../hooks/usePaymentSupport';
import { useSetupAction } from '../hooks/useSetupAction';
import NativePaymentButton from '../components/NativePaymentButton';
import ErrorPopup from '../components/ErrorPopup';
import classes from './classes.module.scss';

// ============================================================================
// Types
// ============================================================================

interface PaymentMethodsListScreenProps {
  paymentMethods: PaymentMethod[];
  /** ID of the currently selected payment method (local state, may differ from API default) */
  selectedMethodId?: number;
  onSelect: (paymentMethod: PaymentMethod) => void;
  onAddClick: (kind: string) => void;
  availableOptions?: PaymentOption[];
  isPayg?: boolean;
  /** When true, a payment action is already in progress — all interactive buttons are disabled */
  actionInProgress?: boolean;
  /** Generic setup callback from PurchaseFlow — routed through useSetupAction */
  onPerformSetup?: (kind: string) => Promise<void>;
  /** Called when a payment method needs editing (expired, reauthorize, security reasons) */
  onEditPaymentMethod?: (paymentMethodId: number, kind: string) => void;
  /** Called on mount to load fresh payment methods from API */
  onLoadMethods?: () => Promise<void>;
}

// ============================================================================
// Sub-components
// ============================================================================

const SkeletonCards: React.FC = () => (
  <div className={ classes.skeletonMethodsList }>
    {[1, 2, 3].map((i) => (
      <div key={ i } className={ classes.skeletonMethodCard } />
    ))}
  </div>
);

/**
 * Returns true if the method has an editable error that should trigger
 * the edit flow instead of normal selection.
 *
 * Matches old flow: expired, reauthorizeRequired, or errorText — except
 * "Cannot be used on this browser" / "Cannot be used at this time" which
 * remain selectable (the buy button is disabled on PurchaseDetailsScreen).
 */
const hasEditableError = (method: PaymentMethod): boolean => {
  if (!method.error && !method.expired && !method.reauthorizeRequired) return false;
  return method.error !== PAYMENT_SOURCE_ERRORS.CANNOT_BE_USED_ON_THIS_BROWSER
    && method.error !== PAYMENT_SOURCE_ERRORS.CANNOT_BE_USED_AT_THIS_TIME;
};

/** Renders the list of saved payment methods (unsupported methods are hidden) */
const SavedMethodsList: React.FC<{
  methods: PaymentMethod[];
  selectedMethodId?: number;
  onSelect: (method: PaymentMethod) => void;
  onEditPaymentMethod?: (paymentMethodId: number, kind: string) => void;
  isPayg: boolean;
  supportMap: Record<string, boolean>;
  renderText: (key: string) => React.ReactNode;
}> = ({
  methods, selectedMethodId, onSelect, onEditPaymentMethod, isPayg, supportMap, renderText
}) => {
  const sorted = [...methods].sort((a, b) => Number(b.isDefault) - Number(a.isDefault));

  const getClickHandler = (method: PaymentMethod): (() => void) | undefined => {
    const paygTextKey = isPayg ? getPaygIneligibilityTextKey(method) : null;
    if (paygTextKey) return undefined;

    if (hasEditableError(method) && onEditPaymentMethod) {
      return () => onEditPaymentMethod(method.id, method.kind);
    }

    return () => onSelect(method);
  };

  return (
    <div className={ classes.savedMethodsList }>
      {sorted.map((method) => {
        if (supportMap[method.kind] === false) return null;

        const adjustedMethod = selectedMethodId
          ? { ...method, isDefault: method.id === selectedMethodId }
          : method;

        const paygTextKey = isPayg ? getPaygIneligibilityTextKey(method) : null;

        return (
          <PaymentMethodCard
            key={ method.id }
            paymentMethod={ adjustedMethod }
            onClick={ getClickHandler(method) }
            showDefaultIndicator
            showError
            showArrow={ false }
            highlightDefault
            paygIneligibleMessage={ paygTextKey ? renderText(paygTextKey) as string : undefined }
          />
        );
      })}
    </div>
  );
};

/**
 * Renders add payment method buttons.
 *
 * Uses the gateway registry to determine which buttons to show,
 * their rendering order, icons, text, and CSS classes — instead of
 * per-kind hardcoded rendering.
 *
 * Native buttons (Apple Pay) are rendered as <apple-pay-button> web components
 * matching the same UI as PurchaseDetailsScreen's PaymentOptionsList.
 */
const AddPaymentButtons: React.FC<{
  options: PaymentOption[];
  visibleKinds: string[];
  onAddClick: (kind: string) => void;
  disabled?: boolean;
}> = ({
  options, visibleKinds, onAddClick, disabled = false
}) => {
  const { renderText, renderIcon } = usePurchaseRender();

  const availableKindSet = new Set<string>(
    options.filter((opt) => opt.available).map((opt) => opt.kind)
  );
  const kindsToShow = visibleKinds.filter((kind) => availableKindSet.has(kind));

  // Separate native-button kinds (Apple Pay) from regular styled buttons
  const nativeKinds = kindsToShow.filter((kind) => getKindConfig(kind)?.nativeButton);
  const regularKinds = kindsToShow.filter((kind) => !getKindConfig(kind)?.nativeButton);

  return (
    <>
      {/* Native buttons (e.g., Apple Pay <apple-pay-button> web component) */}
      {nativeKinds.map((kind) => {
        const config = getKindConfig(kind);
        if (!config?.nativeButton) return null;

        const skeleton = (
          <div className={ classes.addPaymentMethodButtonContainer }>
            <div className={ classes.skeletonButton } />
          </div>
        );

        if (disabled) return <React.Fragment key={ kind }>{skeleton}</React.Fragment>;

        return (
          <NativePaymentButton
            key={ kind }
            kind={ config.nativeButton.kind }
            type={ config.nativeButton.type }
            onClick={ () => onAddClick(kind) }
            skeleton={ skeleton }
          />
        );
      })}

      {/* Regular styled buttons */}
      {regularKinds.map((kind) => {
        const config = getKindConfig(kind);
        if (!config) return null;

        if (disabled) {
          return (
            <div key={ kind } className={ classes.addPaymentMethodButtonContainer }>
              <div className={ classes.skeletonButton } />
            </div>
          );
        }

        const buttonClass = config.addButtonCssModifier
          ? cn(classes.paymentOptionButton, classes[`paymentOptionButton${ config.addButtonCssModifier }`])
          : classes.paymentOptionButton;
        const iconClass = config.addButtonIconCssModifier
          ? cn(classes.paymentOptionIcon, classes[`paymentOptionIcon${ config.addButtonIconCssModifier }`])
          : classes.paymentOptionIcon;

        return (
          <div key={ kind } className={ classes.addPaymentMethodButtonContainer }>
            <button
              type="button"
              onClick={ () => onAddClick(kind) }
              className={ buttonClass }
              data-testid={ `payment-methods-add-${ kind }` }
            >
              <span className={ iconClass }>
                {renderIcon(config.addButtonIconKey)}
              </span>
              {renderText(config.addButtonTextKey)}
            </button>
          </div>
        );
      })}
    </>
  );
};

// ============================================================================
// Main Component
// ============================================================================

const PaymentMethodsListScreen: React.FC<PaymentMethodsListScreenProps> = ({
  paymentMethods,
  selectedMethodId,
  onSelect,
  onAddClick,
  availableOptions = [],
  isPayg = false,
  actionInProgress = false,
  onPerformSetup,
  onEditPaymentMethod,
  onLoadMethods
}) => {
  const { renderText } = usePurchaseRender();
  const [loading, setLoading] = useState(!!onLoadMethods);

  useEffect(() => {
    if (!onLoadMethods) return;
    setLoading(true);
    onLoadMethods().finally(() => setLoading(false));
  }, []);

  const noopSetup = useCallback(() => Promise.resolve(), []);
  const setup = useSetupAction(onPerformSetup || noopSetup, onAddClick);

  const { supportMap, loading: supportLoading } = usePaymentSupport(loading ? [] : paymentMethods);

  const { visibleKinds, loading: visibleKindsLoading } = useVisibleKinds(isPayg);

  const isResolving = loading || supportLoading || visibleKindsLoading;

  const renderMethodsList = () => {
    if (setup.inProgress || isResolving) return <SkeletonCards />;
    if (paymentMethods.length === 0) {
      return (
        <div className={ classes.noSavedMethods } data-testid="payment-methods-empty-state">
          {renderText(PURCHASE_FLOW_TEXT_KEYS.NO_SAVED_METHODS)}
        </div>
      );
    }
    return (
      <SavedMethodsList
        methods={ paymentMethods }
        selectedMethodId={ selectedMethodId }
        onSelect={ onSelect }
        onEditPaymentMethod={ onEditPaymentMethod }
        isPayg={ isPayg }
        supportMap={ supportMap }
        renderText={ renderText }
      />
    );
  };

  return (
    <div className={ classes.methodsListScreenContainer } data-testid="payment-methods-list-screen">
      <ErrorPopup error={ setup.error } onDismiss={ setup.resetError } />

      {/* Saved Payment Methods — scrollable area */}
      <div className={ classes.savedMethodsScrollArea } data-testid="payment-methods-scroll-area">
        {renderMethodsList()}
      </div>

      {/* Separator + Add buttons — sticky at bottom on mobile; hidden while loading (matches old flow) */}
      {!setup.inProgress && !isResolving && (
        <div className={ classes.methodsListFooter } data-testid="payment-methods-footer">
          <div className={ classes.methodsListSeparator } />
          <AddPaymentButtons
            options={ availableOptions }
            visibleKinds={ visibleKinds }
            onAddClick={ setup.handleClick }
            disabled={ actionInProgress }
          />
        </div>
      )}
    </div>
  );
};

export default PaymentMethodsListScreen;
